/**
 * Phase 32 Plan 32-2 — 13-week engineer sparkline per UI-SPEC §246.
 *
 * Compact `<LineChart>` (no axes / no grid). When `isSelf={true}` the line
 * is drawn in the primary blue (#2563eb) to emphasise the self-view; the
 * default leaderboard sparkline uses neutral gray (#9ca3af).
 */
import type { ReactElement } from 'react';
import { Line, LineChart, ResponsiveContainer, Tooltip } from 'recharts';

export interface SparklinePoint {
  week: number;
  value: number;
}

export interface EngineerSparklineProps {
  data: SparklinePoint[];
  isSelf?: boolean;
  height?: number;
}

interface SparklineTooltipPayloadItem {
  payload?: SparklinePoint;
}

function SparklineTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: SparklineTooltipPayloadItem[];
}): ReactElement | null {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs shadow-sm">
      <span className="font-medium text-gray-900">{point.value}</span>{' '}
      <span className="text-gray-600">{point.value === 1 ? 'commit' : 'commits'}</span>
    </div>
  );
}

export function EngineerSparkline({
  data,
  isSelf = false,
  height = 60,
}: EngineerSparklineProps): ReactElement {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
        <Tooltip content={<SparklineTooltip />} cursor={false} />
        <Line
          dataKey="value"
          stroke={isSelf ? '#2563eb' : '#9ca3af'}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
          isAnimationActive={false}
          type="monotone"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default EngineerSparkline;
